import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCustomer } from "../lib/CustomerContext";
import { formatApiErrorDetail } from "../lib/api";
import SEO from "../lib/SEO";

export default function Login() {
    const { login } = useCustomer();
    const navigate = useNavigate();
    const location = useLocation();
    const [form, setForm] = useState({ email: "", password: "" });
    const [submitting, setSubmitting] = useState(false);

    // Where to send the user after login — either wherever they came from, or /account
    const redirect = new URLSearchParams(location.search).get("next") || "/account";

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const c = await login(form.email, form.password);
            toast.success(`Welcome back, ${c.name.split(" ")[0]}`);
            navigate(redirect);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid email or password");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main data-testid="login-page" className="pt-24 md:pt-[10rem] pb-20 bg-[#FFF7FB] min-h-[70vh]">
            <SEO title="Log in — Cake Central" path="/login" />
            <div className="max-w-md mx-auto px-4 sm:px-6">
                <h1 className="font-display text-4xl md:text-5xl text-ink mb-2 leading-none">Log in</h1>
                <p className="text-sm text-ink-soft mb-8">Welcome back — sign in to see your orders.</p>

                <form onSubmit={onSubmit} className="bg-white border border-[#EAE3DE] p-6 space-y-4">
                    <div>
                        <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Email</label>
                        <input
                            type="email"
                            required
                            autoFocus
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="w-full px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush"
                            data-testid="login-email"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Password</label>
                        <input
                            type="password"
                            required
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            className="w-full px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush"
                            data-testid="login-password"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-pink w-full justify-center disabled:opacity-60"
                        data-testid="login-submit"
                    >
                        {submitting ? "Signing in…" : "Log in"}
                    </button>
                </form>

                <p className="text-sm text-ink-soft text-center mt-6">
                    Don't have an account?{" "}
                    <Link to="/signup" className="text-blush hover:underline">Create one</Link>
                </p>
                <p className="text-xs text-ink-soft text-center mt-2">
                    Forgot your password? <Link to="/contact" className="text-blush hover:underline">Contact us</Link>
                </p>
            </div>
        </main>
    );
}
